import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { UserContext } from "../UserContext";
import { Link } from "react-router-dom";
import Comment from "../Comment";
import Post from "../Post";
import config from "../config";

export default function ProfilePage() {
  const { id } = useParams();
  const { userInfo, setUserInfo } = useContext(UserContext);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("profile"); // State to manage active tab
  const [isEditing, setIsEditing] = useState(false);
  const [newBio, setNewBio] = useState("");

  const [userPosts, setUserPosts] = useState([]);
  const [userComments, setUserComments] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [newProfilePic, setNewProfilePic] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    fetch(`${config.apiUrl}/profile/${id}`, {
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("User not found");
        }
        return response.json();
      })
      .then((data) => {
        setProfile(data);
        setNewBio(data.bio);
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(error.message);
      });
  }, [id]);

  const handleProfileUpdate = () => {
    const formData = new FormData();
    formData.append("bio", newBio);
    if (newProfilePic) {
      formData.append("profilePic", newProfilePic);
    }

    fetch(`${config.apiUrl}/profile/${id}`, {
      method: "PUT",
      credentials: "include",
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to update profile");
        }
        return response.json();
      })
      .then((data) => {
        setProfile(data);
        setIsEditing(false);
        setActiveTab("profile"); // Switch back to the profile view after saving
        window.location.reload();
      })
      .catch((error) => {
        console.error(error);
        alert(error);
      });
  };

  function logout() {
    fetch(`${config.apiUrl}/logout`, {
      method: "POST",
      credentials: "include",
    });
    setUserInfo(null);
  }

  if (errorMessage) {
    return <div className="error-message">{errorMessage}</div>;
  }

  if (!profile) {
    return <div>Loading...</div>;
  }

  const isOwnProfile = userInfo && userInfo.id === id;

  const fetchUserHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${config.apiUrl}/history/${id}`, {
        credentials: "include",
      });
      if (!response.ok) {
        const errorText = await response.text(); // Read error message from the response
        throw new Error(
          `Failed to fetch user history: ${response.status} - ${errorText}`,
        );
      }
      const { posts, comments } = await response.json(); // Assuming your API returns an object with posts and comments
      setUserPosts(posts);
      setUserComments(comments);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === "history") {
      fetchUserHistory();
    }
  };

  // Function to render different content based on the selected tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div className="profile-details">
            <div className="profile-picture-section">
              <img
                src={`${config.apiUrl}/${profile.picturePath}`}
                alt="Profile"
                className="profile-picture"
              />
            </div>
            <div className="profile-statistics">
              <p>Bio: {profile.bio}</p>
              <p>Total number of posts: {profile.postCount}</p>
              <p>Total number of comments: {profile.commentCount}</p>
            </div>
          </div>
        );

      case "edit":
        return isOwnProfile ? (
          <div className="edit-profile-section">
            <div className="edit-picture">
              <img
                src={`${config.apiUrl}/${profile.picturePath}`}
                alt="Current Profile"
                className="profile-picture"
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setNewProfilePic(e.target.files[0])}
              />
            </div>
            <div className="bio-editor">
              <p>Edit your bio:</p>
              <textarea
                value={newBio}
                onChange={(e) => setNewBio(e.target.value)}
              />
            </div>
            <div className="button-holder">
              <button onClick={handleProfileUpdate}>Save Changes</button>
              <button
                className="logout-button"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null;

      case "history":
        if (isLoadingHistory) {
          return <div>Loading history...</div>;
        }

        // Combine posts and comments
        const combinedHistory = [
          ...userPosts.map((post) => ({
            ...post,
            type: "post", // Add a type for differentiation
          })),
          ...userComments.map((comment) => ({
            ...comment,
            type: "comment", // Add a type for differentiation
          })),
        ];

        // Sort by createdAt timestamp (most recent first)
        combinedHistory.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        return (
          <div className="history-content">
            <h3>User Activity</h3>
            {combinedHistory.length === 0 ? (
              <p>No activity available.</p>
            ) : (
              combinedHistory.map((activity) => {
                if (activity.type === "post") {
                  return (
                    <Post
                      key={activity._id} // Use the correct identifier for Post
                      _id={activity._id}
                      title={activity.title}
                      summary={activity.summary}
                      path_cover={activity.path_cover}
                      author={activity.author}
                      createdAt={activity.createdAt}
                      updatedAt={activity.updatedAt}
                      isCompact={true}
                    />
                  );
                } else {
                  return (
                    <Link
                      to={`/post/${activity.postId}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <Comment
                        key={activity._id}
                        postId={activity.postId}
                        author={activity.author}
                        userId={activity.userId}
                        authorPicturePath={activity.authorPicturePath}
                        text={activity.text}
                        date={activity.createdAt}
                        commentId={activity._id}
                        isActionEnabled={false}
                      />
                    </Link>
                  );
                }
              })
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>{profile.username}'s Profile</h2>
      </div>

      <div className="profile-content">
        <div className="profile-sidebar">
          <ul className="profile-options">
            <li>
              <a
                href="#"
                className={activeTab === "profile" ? "active" : ""}
                onClick={() => setActiveTab("profile")}
              >
                Profile
              </a>
            </li>
            {isOwnProfile && (
              <li>
                <a
                  href="#"
                  className={activeTab === "edit" ? "active" : ""}
                  onClick={() => setActiveTab("edit")}
                >
                  Edit Profile
                </a>
              </li>
            )}
            <li>
              <a
                href="#"
                className={activeTab === "history" ? "active" : ""}
                onClick={() => handleTabClick("history")}
              >
                View History
              </a>
            </li>
            {isOwnProfile && (
              <li>
                <a href="#" className="logout" onClick={logout}>
                  Logout
                </a>
              </li>
            )}
          </ul>
        </div>

        <div className="profile-tab-content">{renderTabContent()}</div>
      </div>
    </div>
  );
}
